import {
  IconWarning,
  Modal,
  ModalButton,
  ModalContent,
  ModalDismiss,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Text,
  useModalState,
} from '@vtex/admin-ui'
import {
  type IncompatibleField as IncompatibleFieldType,
  useFeature,
  useIncompatible,
} from 'hooks'
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react'
import { FormattedMessage } from 'react-intl'
import { Event, WatchedIncompatibleField } from 'typings'
import { IncompatibleTable, useFormStateContext } from 'ui'
import { DRAWER_ZINDEX } from 'ui/components/Drawer/drawer.css'
import { useAnalytics } from 'use-analytics'
import {
  BuyAndWinBenefitVariant,
  MoreForLessBenefitVariant,
  RegularBenefitVariant,
} from '../typings'
import { fieldMessages, incompatibilityRules } from './constants'
import { messages } from './messages'
import styles from './styles.css'

type BenefitVariants =
  | RegularBenefitVariant
  | BuyAndWinBenefitVariant
  | MoreForLessBenefitVariant

type Props = {
  onConfirm: (benefit: BenefitVariants) => void
}
export const BenefitChangeModal = forwardRef<BenefitChangeModalRef, Props>(
  (props, ref) => {
    const { onConfirm } = props

    const [benefit, setBenefit] = useState<BenefitVariants>()

    const modalState = useModalState()

    const { getFeature } = useFeature()

    const { track } = useAnalytics()

    const { form } = useFormStateContext()

    const benefitFlag = getFeature('benefit_modal')

    const { checkIncompatible } = useIncompatible<BenefitVariants>({
      rules: incompatibilityRules,
      onlyFilled: true,
      form,
    })

    const [incompatibleFields, setIncompatibleFields] = useState<
      IncompatibleFieldType[]
    >([])

    const hasIncompatibilities = !!incompatibleFields.length

    const sendEvent = useCallback(
      (eventName: Event) => {
        track(eventName, {
          watchedValue: benefit,
          watchedField: WatchedIncompatibleField.BENEFIT,
          incompatibleFields,
        })
      },
      [benefit, incompatibleFields, track]
    )

    useImperativeHandle(
      ref,
      () => ({
        onBenefitChange: (newBenefit) => {
          if (benefitFlag) {
            setBenefit(newBenefit)
            const { incompatibleFields, hasIncompatibilities } =
              checkIncompatible(newBenefit as BenefitVariants)
            if (hasIncompatibilities) {
              setIncompatibleFields(incompatibleFields)
              modalState.show()
              return
            }
          }
          onConfirm(newBenefit as BenefitVariants)
        },
      }),
      [benefitFlag, checkIncompatible, modalState, onConfirm]
    )

    const handleCancel = () => {
      sendEvent(Event.INCOMPATIBLE_MODAL_CANCEL_CLICK)
      modalState.hide()
    }

    const handleConfirm = () => {
      if (!benefit) {
        return
      }
      sendEvent(Event.INCOMPATIBLE_MODAL_CONFIRM_CLICK)
      modalState.hide()
      onConfirm(benefit)
    }

    useEffect(() => {
      if (modalState.open) {
        sendEvent(Event.INCOMPATIBLE_MODAL_VIEW)
      }
    }, [modalState.open, sendEvent])

    if (!benefitFlag || !hasIncompatibilities) {
      return null
    }

    return (
      <Modal state={modalState} portal style={{ zIndex: DRAWER_ZINDEX }}>
        <ModalHeader className={styles.header}>
          <ModalTitle className={styles.title}>
            <IconWarning className={styles.icon} />
            <FormattedMessage {...messages.title} />
          </ModalTitle>
          <ModalDismiss onClick={handleCancel} />
        </ModalHeader>
        <ModalContent>
          <Text>
            <FormattedMessage {...messages.subtitle} />
          </Text>
          <IncompatibleTable
            fieldMessages={fieldMessages}
            incompatibleFields={incompatibleFields}
            hasIncompatibilities={hasIncompatibilities}
          />
          <Text className={styles.continue}>
            <FormattedMessage {...messages.continue} />
          </Text>
        </ModalContent>

        <ModalFooter>
          <ModalButton
            variant="secondary"
            onClick={handleCancel}
            dismissModal
            size="large"
          >
            <FormattedMessage {...messages.footer.cancel} />
          </ModalButton>
          <ModalButton size="large" onClick={handleConfirm}>
            <FormattedMessage {...messages.footer.confirm} />
          </ModalButton>
        </ModalFooter>
      </Modal>
    )
  }
)

BenefitChangeModal.displayName = 'BenefitChangeModal'

export type BenefitChangeModalRef = {
  onBenefitChange: (benefit: BenefitVariants) => void
}
